import React, { useEffect, useRef } from 'react'
import Group from 'components/utils/Group'
import BodyText from './Typography/BodyText'
import SlideDown from 'components/Common/SlideDown/SlideDown'
import Divider from './Divider'
import useToggle from 'hooks/useToggle'
import Clickable from 'components/Common/Clickable/Clickable'
import DropdownChevron from './Dropdown/DropdownChevron'
import styled from 'styled-components'
import { rem } from 'polished'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyTextBlock from './TextBlocks/BodyTextBlock'
import { useLocation } from 'react-router'

const AccordionContainer = styled.div`
  & + & {
    margin-top: ${rem(12)};
  }
`

const AccordionClickArea = styled(Clickable)`
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    background: transparent;
    pointer-events: none;
    border-radius: ${props => props.theme.borderRadius.medium};
    top: -4px;
    right: -4px;
    bottom: -4px;
    left: -4px;
    box-shadow: none;
    transition: box-shadow 0.2s;
  }

  .input-keyboard &:focus-within::before {
    box-shadow: 0 0 0 2px ${props => props.theme.palette.neutral.five};
  }
`

// We want our accordion to have a 16px gap between content and the title
// except when it's closed. So we manage this by inserting another element *within*
// the slide down component so it's part of the content that gets shown/hidden
const AccordionSpacer = styled.div`
  height: ${rem(16)};
`

const AccordionDivider = styled(Divider)`
  margin-top: ${rem(16)};
`

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, 'size' | 'onClick'> {
  heading: string;
  subtitle?: string;
  defaultOpen?: boolean;
  icon?: React.ReactElement;
  open?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onOpen?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onClose?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  size?: 'small' | 'large';
  /**
   * Whether or not to scroll to the content when this accordion is opened
   */
  scrollOnOpen?: boolean;
  /**
   * Whether or not to automatically open if we see ourselves being
   * linked by the url hash
   */
  openOnHash?: boolean;
}

function Accordion(props: Props) {
  const {
    heading,
    subtitle,
    children,
    defaultOpen,
    open,
    onClick,
    size,
    icon,
    onOpen,
    onClose,
    scrollOnOpen,
    openOnHash,
    ...containerProps
  } = props

  const [uncontrolledOpen, toggleUncontrolledOpen, setOpen] = useToggle(defaultOpen)
  const isControlled = open !== undefined
  const containerRef = useRef<HTMLDivElement>(null)
  const actuallyOpen = isControlled ? open : uncontrolledOpen

  const onTitleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isControlled) {
      toggleUncontrolledOpen()
    }

    if (actuallyOpen) {
      onClose?.(e)
    } else {
      onOpen?.(e)
    }

    onClick?.(e)
  }

  useEffect(() => {
    if (scrollOnOpen && actuallyOpen) {
      containerRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }, [actuallyOpen, scrollOnOpen])

  const location = useLocation()
  useEffect(() => {
    if (openOnHash && !!location.hash && location.hash === `#${containerProps.id}`) {
      setOpen()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  return <AccordionContainer {...containerProps} ref={containerRef}>
    <AccordionClickArea onClick={onTitleClick}>
      <Group
        direction="horizontal"
        verticalAlign="center"
        horizontalAlign="space-between"
        gap={16}
      >
        <VerticalSpacer gap={4}>
          {!!icon && <BodyTextBlock
            startIcon={icon}
            variant={size === 'large' ? 'large' : 'medium'}
            weight="bold"
          >
            {heading}
          </BodyTextBlock>}
          {!icon && <BodyText
            variant={size === 'large' ? 'large' : 'medium'}
            weight="bold"
          >
            {heading}
          </BodyText>}
          {subtitle && <BodyText variant={size === 'large' ? 'medium' : 'small'}>{subtitle}</BodyText>}
        </VerticalSpacer>
        <DropdownChevron size="M" direction={actuallyOpen ? 'up' : 'down'} />
      </Group>
    </AccordionClickArea>
    <SlideDown show={actuallyOpen} animateOpacity>
      <AccordionSpacer />
      {children}
    </SlideDown>
    <AccordionDivider kind="primary" />
  </AccordionContainer>
}

export default Accordion
